// site-tour.service.ts
import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { catchError, map, Observable, throwError } from 'rxjs';
import { environment } from 'src/environments/environment';
import { ErrorMessages } from '@app/shared/constants';

export interface SiteTour {
  id?: number;
  name: string;
  multipage?: number;
  pageSlug?: string;
  settings?: any;
  isActive?: number;
  steps?: SiteTourStep[];
}

export interface SiteTourStep {
  id?: number;
  site_tour_id?: number;
  title: string;
  content: string;
  pageSlug?: string;
  elementSelector?: string;
  linkUrl?: string;
  linkLabel?: string;
  settings?: any;
}

export interface UpdateTourWithStepsRequest {
  tourData?: Partial<SiteTour>;
  create?: Partial<SiteTourStep>[];
  update?: { id: number; data: Partial<SiteTourStep> }[];
  delete?: number[];
}

@Injectable({
  providedIn: 'root',
})
export class SiteTourService {
  private apiUrl = `${environment.APP_SERVICES_URL}/admin/`;

  constructor(private http: HttpClient) {}

  // Tour operations
  getAllTours(): Observable<SiteTour[]> {
    return this.http.get<SiteTour[]>(`${this.apiUrl}site-tours`).pipe(
      map((res: any) => {
        return res;
      }),
      catchError((error: any) => throwError(error.error || ErrorMessages.UNEXPECTED))
    );
  }

  deleteTour(id: number): Observable<void> {
    return this.http.delete<void>(`${this.apiUrl}site-tours/${id}`).pipe(
      map((res: any) => {
        return res;
      }),
      catchError((error: any) => throwError(error.error || ErrorMessages.UNEXPECTED))
    );
  }

  activateTour(id: number): Observable<SiteTour> {
    return this.http.put<SiteTour>(`${this.apiUrl}site-tours/${id}/activate`, {}).pipe(
      map((res: any) => {
        return res;
      }),
      catchError((error: any) => throwError(error.error || ErrorMessages.UNEXPECTED))
    );
  }

  deactivateTour(id: number): Observable<SiteTour> {
    return this.http.put<SiteTour>(`${this.apiUrl}site-tours/${id}/deactivate`, {}).pipe(
      map((res: any) => {
        return res;
      }),
      catchError((error: any) => throwError(error.error || ErrorMessages.UNEXPECTED))
    );
  }

  // Combined operations
  createTourWithSteps(tour: Partial<SiteTour>, steps: Partial<SiteTourStep>[]): Observable<SiteTour> {
    return this.http.post<SiteTour>(`${this.apiUrl}site-tours/with-steps`, { tourData: tour, steps }).pipe(
      map((res: any) => {
        return res;
      }),
      catchError((error: any) => throwError(error.error || ErrorMessages.UNEXPECTED))
    );
  }

  updateTourWithSteps(id: number, request: UpdateTourWithStepsRequest): Observable<SiteTour> {
    return this.http.put<SiteTour>(`${this.apiUrl}site-tours/${id}/with-steps`, request).pipe(
      map((res: any) => {
        return res;
      }),
      catchError((error: any) => throwError(error.error || ErrorMessages.UNEXPECTED))
    );
  }

  // Hotspot operations
  getAllHotspots(): Observable<any> {
    return this.http.get<any>(`${this.apiUrl}hotspots`).pipe(
      map((res: any) => {
        return res;
      }),
      catchError((error: any) => throwError(error.error || ErrorMessages.UNEXPECTED))
    );
  }
  createHotspot(params: any): Observable<any> {
    return this.http.post<any>(`${this.apiUrl}hotspots`, params).pipe(
      map((res: any) => {
        return res;
      }),
      catchError((error: any) => throwError(error.error || ErrorMessages.UNEXPECTED))
    );
  }

  deleteHotspot(id: number): Observable<void> {
    return this.http.delete<void>(`${this.apiUrl}hotspots/${id}`).pipe(
      map((res: any) => {
        return res;
      }),
      catchError((error: any) => throwError(error.error || ErrorMessages.UNEXPECTED))
    );
  }

  toggleHotspot(id: number): Observable<any> {
    return this.http.patch<any>(`${this.apiUrl}hotspots/${id}/toggle`, {}).pipe(
      map((res: any) => {
        return res;
      }),
      catchError((error: any) => throwError(error.error || ErrorMessages.UNEXPECTED))
    );
  }

  updateHotspot(id: number, data: any): Observable<any> {
    return this.http.patch<any>(`${this.apiUrl}hotspots/${id}`, data).pipe(
      map((res: any) => {
        return res;
      }),
      catchError((error: any) => throwError(error.error || ErrorMessages.UNEXPECTED))
    );
  }
}
